// Utility per rilevare bot e crawler

export interface BotDetectionResult {
  isBot: boolean;
  botName: string | null;
  confidence: 'high' | 'medium' | 'low';
  detectionMethod: 'userAgent' | 'browserFeatures' | 'behaviorPattern';
}

export class BotDetector {
  private allowedBots: string[];

  constructor(allowedBots: string[] = []) {
    this.allowedBots = allowedBots.map(bot => bot.toLowerCase());
  }

  // Rileva se l'user agent appartiene a un bot
  detectFromUserAgent(userAgent: string = navigator.userAgent): BotDetectionResult {
    const ua = userAgent.toLowerCase();
    
    // Cerca corrispondenze esatte con bot conosciuti
    for (const bot of this.allowedBots) {
      if (ua.includes(bot)) {
        return {
          isBot: true,
          botName: bot,
          confidence: 'high',
          detectionMethod: 'userAgent'
        };
      }
    }

    // Pattern aggiuntivi per bot comuni
    const commonBotPatterns = [
      /bot[\s\-_]?/i,
      /crawler[\s\-_]?/i,
      /spider[\s\-_]?/i,
      /scraper[\s\-_]?/i,
      /fetch[\s\-_]?/i,
      /monitor[\s\-_]?/i,
      /check[\s\-_]?/i,
      /test[\s\-_]?/i,
      /scan[\s\-_]?/i,
    ];

    for (const pattern of commonBotPatterns) {
      if (pattern.test(ua)) {
        return {
          isBot: true,
          botName: 'unknown-bot',
          confidence: 'medium',
          detectionMethod: 'userAgent'
        };
      }
    }

    return {
      isBot: false,
      botName: null,
      confidence: 'high',
      detectionMethod: 'userAgent'
    };
  }

  // Rileva bot tramite caratteristiche del browser
  detectFromBrowserFeatures(): BotDetectionResult {
    // Ogni elemento è TRUE se indica un bot
    const suspiciousIndicators = [
      window.navigator.webdriver === true,                      // Selenium/WebDriver
      typeof (window as any).phantom !== 'undefined',           // PhantomJS
      typeof (window as any)._phantom !== 'undefined',          // PhantomJS
      typeof (window as any).callPhantom !== 'undefined',       // PhantomJS
      !navigator.languages || navigator.languages.length === 0, // Nessuna lingua configurata
      navigator.hardwareConcurrency === 0,                      // Nessun core CPU
    ];

    const suspiciousCount = suspiciousIndicators.filter(Boolean).length;

    if (suspiciousCount >= 2) {
      return {
        isBot: true,
        botName: 'headless-browser',
        confidence: 'medium',
        detectionMethod: 'browserFeatures'
      };
    }

    return {
      isBot: false,
      botName: null,
      confidence: 'low',
      detectionMethod: 'browserFeatures'
    };
  }

  // Rileva bot tramite pattern comportamentali
  detectFromBehavior(): BotDetectionResult {
    // Controlla se la finestra ha dimensioni tipiche di bot
    const windowSize = window.innerWidth * window.innerHeight;
    const isHeadless = windowSize === 0;
    
    // Controlla se ci sono eventi mouse/touch recenti
    const hasRecentInteraction = this.checkRecentInteraction();
    
    if (isHeadless || !hasRecentInteraction) {
      return {
        isBot: true,
        botName: 'automated-browser',
        confidence: 'low',
        detectionMethod: 'behaviorPattern'
      };
    }

    return {
      isBot: false,
      botName: null,
      confidence: 'low',
      detectionMethod: 'behaviorPattern'
    };
  }

  private checkRecentInteraction(): boolean {
    // Questo è un controllo semplificato
    // In una implementazione reale, potresti tenere traccia degli eventi di interazione
    return document.hasFocus();
  }

  // Metodo principale per rilevare bot
  detectBot(): BotDetectionResult {
    // Priorità: User Agent > Browser Features > Behavior
    const userAgentResult = this.detectFromUserAgent();
    if (userAgentResult.isBot) {
      return userAgentResult;
    }

    const browserFeaturesResult = this.detectFromBrowserFeatures();
    if (browserFeaturesResult.isBot && browserFeaturesResult.confidence === 'medium') {
      return browserFeaturesResult;
    }

    // Il rilevamento comportamentale è meno affidabile, quindi lo usiamo solo come ultima risorsa
    return {
      isBot: false,
      botName: null,
      confidence: 'high',
      detectionMethod: 'userAgent'
    };
  }

  // Verifica se un bot specifico è nella lista consentiti
  isBotAllowed(botName: string): boolean {
    return this.allowedBots.includes(botName.toLowerCase());
  }

  // Aggiorna la lista dei bot consentiti
  updateAllowedBots(bots: string[]): void {
    this.allowedBots = bots.map(bot => bot.toLowerCase());
  }
}

export default BotDetector;
